import { Droppable } from "@hello-pangea/dnd";
import { AddRounded, InfoOutlined, MoreVertRounded } from "@mui/icons-material";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  styled,
  useTheme,
} from "@mui/material";
import { DateTime } from "luxon";
import React, { useMemo, useState } from "react";
import {
  Accordion,
  ActionItem,
  ActionItemsTable,
  CreateActionItemModal,
  DraggableActionItem,
} from "../../../../components";
import { ClosedActionItemStatuses } from "../../../../constants";
import { useSession } from "../../../../hooks";
import { useDeleteSection } from "../../../../http";
import { IBlock, ISection } from "../../../../models";
import { useDestructiveConfirm } from "../../../../utilities";
import { EditSectionModal } from "./EditSectionModal";
import { usePlanContext } from "./PlanContext";

const DroppableZone = styled(Stack)<{ $isDraggingOver: boolean; $isPastView: boolean }>`
  transition: ${({ theme }) => theme.transitions.create("background-color")};

  background-color: ${({ $isDraggingOver, $isPastView, theme }) =>
    $isDraggingOver ? theme.palette.primary.opacity5 : $isPastView ? "transparent" : theme.palette.common.white};
  pointer-events: ${({ $isDraggingOver }) => ($isDraggingOver ? "none" : "auto")};
`;

interface IProps {
  teamName: string;
  teamSlug: string;
  section: ISection;
  block: IBlock;
  isPastView: boolean;
  canEditPlan: boolean;
  isTableView: boolean;
  onEditPlanStructureClicked: () => void;
}

const Section = (props: IProps) => {
  const [isHovering, setIsHovering] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [showClosed, setShowClosed] = useState(false);
  const destructiveConfirm = useDestructiveConfirm();
  const { mutate: deleteSection } = useDeleteSection();
  const user = useSession();
  const { planGuid } = usePlanContext();
  const [isEditSectionModalVisible, setIsEditSectionModalVisible] = useState(false);
  const [isCreateActionItemModalVisible, setIsCreateActionItemModalVisible] = useState(false);
  const [focusedActionItemGuid, setFocusedActionItemGuid] = useState<string | undefined>(undefined);
  const { fetchActionItemSubitems } = usePlanContext();
  const theme = useTheme();

  const sortedActiveItems = useMemo(
    () =>
      props.section.actionItems
        .filter((actionItem) => !ClosedActionItemStatuses.includes(actionItem.currentStatus))
        .sort((a, b) => a.sortOrder.localeCompare(b.sortOrder)) ?? [],
    [props.section.actionItems]
  );
  const sortedClosedItems = useMemo(
    () =>
      props.section.actionItems
        .filter((actionItem) => ClosedActionItemStatuses.includes(actionItem.currentStatus))
        .sort((a, b) =>
          DateTime.fromISO(a.statusLastModifiedDateTimeUtc, { zone: "utc" }) <
          DateTime.fromISO(b.statusLastModifiedDateTimeUtc, { zone: "utc" })
            ? 1
            : -1
        ) ?? [],
    [props.section.actionItems]
  );

  function handleMenuItemClicked(e: React.MouseEvent) {
    setMenuAnchor(null);
    e.stopPropagation();
  }

  async function handleAddActionItemClicked(e: React.MouseEvent) {
    setIsCreateActionItemModalVisible(true);
    e.stopPropagation();
  }

  async function handleEditClicked(e: React.MouseEvent) {
    handleMenuItemClicked(e);
    setIsEditSectionModalVisible(true);
  }

  async function handleDeleteClicked(e: React.MouseEvent) {
    handleMenuItemClicked(e);

    const { confirmed } = await destructiveConfirm({
      title: "Delete Section",
      content: "Deleting this section will delete all of its items as well.",
      confirmationText: "Delete",
      cancellationText: "Cancel",
    });

    if (!confirmed) {
      return;
    }

    deleteSection({
      planGuid: planGuid,
      guid: props.section.guid,
      modifiedByUserId: user.userId,
    });
  }

  return (
    <Stack>
      <Droppable key={props.section.guid} droppableId={props.section.guid}>
        {(provided, snapshot) => (
          <DroppableZone
            $isPastView={props.isPastView}
            $isDraggingOver={snapshot.isDraggingOver}
            {...provided.droppableProps}
            ref={provided.innerRef}
            onMouseEnter={() => {
              setIsHovering(true);
            }}
            onMouseLeave={() => {
              setIsHovering(false);
            }}
          >
            <Accordion
              title={
                <Stack direction="row" sx={{ alignItems: "center", minHeight: "35px", width: "100%" }}>
                  <Stack direction="row" spacing={0.25} flexGrow={1} alignItems="top">
                    <Typography variant="body2" sx={{ color: "grey.400" }}>
                      {props.section.name}
                    </Typography>
                    {!isExpanded && (
                      <Typography variant="caption" sx={{ position: "relative", top: 1 }}>
                        ({sortedActiveItems.length} Active, {sortedClosedItems.length} Closed)
                      </Typography>
                    )}
                    {props.section.hintText && (
                      <Tooltip title={props.section.hintText}>
                        <InfoOutlined sx={{ color: "grey.300", alignSelf: "center" }} />
                      </Tooltip>
                    )}
                  </Stack>

                  {(isHovering || !window.matchMedia("(pointer: fine)").matches) &&
                    !snapshot.isDraggingOver &&
                    props.canEditPlan &&
                    (props.section.isEditable || props.section.createActionItemAllowed) && (
                      <>
                        {props.section.createActionItemAllowed && (
                          <IconButton size="small" onClick={handleAddActionItemClicked}>
                            <AddRounded />
                          </IconButton>
                        )}

                        {props.section.isEditable && (
                          <>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setMenuAnchor(e.currentTarget);
                              }}
                            >
                              <MoreVertRounded />
                            </IconButton>
                            <Menu
                              anchorEl={menuAnchor}
                              open={Boolean(menuAnchor)}
                              onClose={(e: React.PointerEvent) => {
                                setMenuAnchor(null);
                                e.stopPropagation();
                              }}
                            >
                              <MenuItem onClick={handleEditClicked}>Edit Section</MenuItem>
                              <MenuItem
                                onClick={(e) => {
                                  handleMenuItemClicked(e);
                                  props.onEditPlanStructureClicked();
                                }}
                              >
                                Reorder Blocks And Sections
                              </MenuItem>
                              <MenuItem onClick={handleDeleteClicked} sx={{ color: theme.palette.error.main }}>
                                Delete Section
                              </MenuItem>
                            </Menu>
                          </>
                        )}
                      </>
                    )}
                </Stack>
              }
              expanded={isExpanded}
              onExpandClicked={() => {
                if (showClosed && isExpanded) {
                  setShowClosed(false);
                }

                setIsExpanded(!isExpanded);
              }}
              backgroundColorOnHover={props.isPastView ? "transparent" : undefined}
            >
              {sortedActiveItems.length > 0 ? (
                props.isTableView ? (
                  <ActionItemsTable
                    tableId={`map-${planGuid}`}
                    actionItems={sortedActiveItems}
                    enableActions
                    fetchActionItemSubitems={fetchActionItemSubitems}
                  />
                ) : (
                  sortedActiveItems.map(
                    (actionItem, index) =>
                      !ClosedActionItemStatuses.includes(actionItem.currentStatus) && (
                        <DraggableActionItem
                          key={actionItem.guid}
                          index={index}
                          actionItemGuid={actionItem.guid}
                          canEdit={actionItem.userCanEdit && !props.isPastView}
                          canEditPlan={props.canEditPlan}
                          isPastView={props.isPastView}
                          isFocused={actionItem.guid === focusedActionItemGuid}
                          onBlur={() => setFocusedActionItemGuid(undefined)}
                        />
                      )
                  )
                )
              ) : (
                <Box sx={{ p: 0.5, pl: 1 }}>
                  <Typography variant="body1" sx={{ color: theme.palette.grey[300] }}>
                    Nothing here yet.
                  </Typography>
                </Box>
              )}
            </Accordion>
            {provided.placeholder}
          </DroppableZone>
        )}
      </Droppable>

      {sortedClosedItems.length > 0 && isExpanded && (
        <>
          {sortedActiveItems.length === 0 && <Divider variant="dashed" orientation="horizontal" sx={{ mx: 1 }} />}

          <Box sx={{ px: 1, my: 0.5 }}>
            <Button variant="tertiary" size="small" onClick={() => setShowClosed(!showClosed)}>
              {!showClosed ? `Show Closed (${sortedClosedItems.length})` : `Hide Closed`}
            </Button>
          </Box>
        </>
      )}

      {sortedClosedItems.length > 0 && showClosed && (
        <>
          <Divider variant="dashed" orientation="horizontal" sx={{ mx: 1 }} />
          <Stack divider={<Divider variant="dashed" orientation="horizontal" sx={{ mx: 1 }} />}>
            {props.isTableView ? (
              <ActionItemsTable
                tableId={`strategicMap-${planGuid}`}
                actionItems={sortedClosedItems}
                fetchActionItemSubitems={fetchActionItemSubitems}
              />
            ) : (
              sortedClosedItems.map((actionItem, _) => (
                <ActionItem
                  key={actionItem.guid}
                  actionItemGuid={actionItem.guid}
                  canEdit={actionItem.userCanEdit && !props.isPastView}
                  canEditPlan={props.canEditPlan}
                  isPastView={props.isPastView}
                  isFocused={actionItem.guid === focusedActionItemGuid}
                  onBlur={() => setFocusedActionItemGuid(undefined)}
                  sx={{ px: 1 }}
                />
              ))
            )}
          </Stack>
          <Divider variant="dashed" orientation="horizontal" sx={{ mx: 1 }} />
        </>
      )}

      <EditSectionModal
        visible={isEditSectionModalVisible}
        section={props.section}
        blockGuid={props.block.guid}
        onSaved={() => setIsEditSectionModalVisible(false)}
        onCancelled={() => setIsEditSectionModalVisible(false)}
      />

      <CreateActionItemModal
        visible={isCreateActionItemModalVisible}
        teamPlanSection={{
          teamName: props.teamName,
          teamSlug: props.teamSlug,
          planGuid: planGuid,
          sectionName: props.section.name,
          sectionGuid: props.section.guid,
        }}
        onCreated={() => {
          setIsCreateActionItemModalVisible(false);
        }}
        onCancelled={() => setIsCreateActionItemModalVisible(false)}
      />
    </Stack>
  );
};

export { Section };
